import {
  WSDivider,
  WSGrid,
  WSList,
  WSLoader,
  WSPage,
  WSPageToolbar,
  WSScreen
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useQueryInternalAccounts } from "../../../query/banking/queries/useQueryInternalAccounts";
import { WSQueries } from "../../../query/WSQuery";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { PATH_ONBOARDING_TAX_WITHHOLDINGS } from "../../Onboarding";
import { PayoutSplits } from "../../Settings/components/PayoutSplits";
import { WingspanAccounts } from "../../Settings/components/WingspanAccounts";
import { BannerBankingVerification } from "../../Wallet/components/BannerBankingVerification";
import { AvailableBalance } from "../components/AvailableBalance";
import { CardTaxWithholdingStatus } from "../components/CardTaxWithholdingStatus";
import { PreviewInstructions } from "../components/PreviewContent";
import { TaxesTransactions } from "../components/Transactions/TaxesTransactions";
import { useIsTaxWithholdingsSetupComplete } from "../hooks/useIsTaxWithholdingsSetupComplete";
import { useTaxPaymentScheduling } from "../hooks/useTaxPaymentScheduling";
import { PATH_TAXES_SCHEDULE_PAYMENT } from "../paths";
import { useGoToTransfer } from "../../Transfer/paths";
import { InfoBoxCCorpTaxLimitations } from "../../../shared/components/InfoBoxCCorpTaxLimitations";

export const RouteIndex: React.FC = () => {
  const history = useHistory();
  const goToTransfer = useGoToTransfer();

  useBrowserPageTitle("Tax Withholding");
  const queryInternalAccounts = useQueryInternalAccounts();
  const {
    hasScheduledTaxPayment,
    hasProcessingTaxPayment,
    isLoading: isTaxPaymentSchedulingLoading
  } = useTaxPaymentScheduling();

  return (
    <WSQueries
      queries={{
        queryInternalAccounts
      }}
      renderLoader={() => <Loading />}
    >
      {({ queryInternalAccounts: { data: internalAccounts } }) => {
        const activeTaxWithholdingAccount = internalAccounts?.find(
          selectorActiveTaxWithholdingAccount
        );

        if (!activeTaxWithholdingAccount) {
          return <Preview />;
        }

        return (
          <WSPage>
            <WSPageToolbar
              title="Tax withholdings"
              actions={[
                {
                  label: "Schedule tax payment",
                  buttonKind: "Secondary",
                  hidden:
                    isTaxPaymentSchedulingLoading ||
                    hasScheduledTaxPayment ||
                    hasProcessingTaxPayment,
                  onClick: () => {
                    history.push(PATH_TAXES_SCHEDULE_PAYMENT);
                  }
                },
                {
                  label: "Transfer",
                  hideButtonOn: false,
                  buttonKind: "Primary",
                  onClick: () => {
                    goToTransfer({ balance: "taxWithholdings" });
                  }
                }
              ]}
            />

            <WSScreen.Mobile>
              <MobileContent
                internalAccountId={
                  activeTaxWithholdingAccount.internalAccountId
                }
              />
            </WSScreen.Mobile>

            <WSScreen.TabletAndDesktop>
              <DesktopContent
                internalAccountId={
                  activeTaxWithholdingAccount.internalAccountId
                }
              />
            </WSScreen.TabletAndDesktop>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};

const Loading: React.FC = () => (
  <WSPage>
    <WSPageToolbar title="Tax withholdings" />
    <WSLoader.Spinner />
  </WSPage>
);

const Preview: React.FC = () => {
  const history = useHistory();

  const setupComplete = useIsTaxWithholdingsSetupComplete();

  return (
    <WSPage>
      <WSPageToolbar
        title="Tax withholdings"
        actions={
          setupComplete
            ? undefined
            : [
                {
                  label: "Setup tax withholdings",
                  buttonKind: "Primary",
                  onClick: () => {
                    history.push(PATH_ONBOARDING_TAX_WITHHOLDINGS);
                  }
                }
              ]
        }
      />

      <WSGrid gutter="2XL">
        <WSGrid.Item span={{ s: "12", m: "8" }}>
          <WSList gap="2XL">
            <InfoBoxCCorpTaxLimitations />

            <BannerBankingVerification />

            <AvailableBalance />

            <PreviewInstructions />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ s: "12", m: "4" }}>
          <WSList gap="2XL">
            <CardTaxWithholdingStatus />

            <WingspanAccounts accountType={"TaxWithholding"} />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};

const DesktopContent: React.FC<{
  internalAccountId: string;
}> = props => (
  <WSGrid gutter="2XL">
    <WSGrid.Item span={{ s: "12", m: "8" }}>
      <WSList gap="2XL">
        <InfoBoxCCorpTaxLimitations />
        <AvailableBalance internalAccountId={props.internalAccountId} />
        <TaxesTransactions />
      </WSList>
    </WSGrid.Item>

    <WSGrid.Item span={{ s: "12", m: "4" }}>
      <WSList gap="2XL">
        <CardTaxWithholdingStatus />

        <PayoutSplits />

        <WSDivider />

        <WingspanAccounts accountType={"TaxWithholding"} />
      </WSList>
    </WSGrid.Item>
  </WSGrid>
);

const MobileContent: React.FC<{
  internalAccountId: string;
}> = props => (
  <WSList gap="2XL">
    <InfoBoxCCorpTaxLimitations />

    <AvailableBalance internalAccountId={props.internalAccountId} />

    <CardTaxWithholdingStatus />

    <TaxesTransactions />

    <PayoutSplits />

    <WSDivider />

    <WingspanAccounts accountType={"TaxWithholding"} />
  </WSList>
);
